import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { memo, useMemo } from 'react';

import { DataTable, type Column } from '@/components/ui/DataTable';
import { RowActionsMenu, type RowAction } from '@/components/ui/RowActionsMenu';
import { StatusChip } from '@/components/ui/StatusChip';
import { formatDate } from '@/lib/organization';

import { DocumentCount } from './DocumentCount';
import { ACTION_LABELS, actionsFor, type DocumentActionId } from './documentActions';
import {
  placementWord,
  replacementPending,
  sizeInWords,
  stateTone,
  stateWord,
  type DocumentRow,
} from './documentLabels';
import { PAGE_SIZE } from './useDocuments';

interface DocumentsTableProps {
  rows: DocumentRow[];
  loading: boolean;
  /** True while the next page is on its way and this one is still shown. */
  refreshing?: boolean;
  error: string | null;
  onRetry: () => void;
  page: number;
  total: number;
  onPageChange: (page: number) => void;
  /** The days the Views and Downloads columns cover, named in their headers. */
  range: number;
  writable: boolean;
  /** True when a search, a filter or a tab narrows the list. */
  filtered: boolean;
  onAction: (action: DocumentActionId, row: DocumentRow) => void;
}

/**
 * The library's table (PB2): one row per document with its state word, its
 * numbers for the chosen range, and the actions the row offers.
 *
 * Memoised, and its columns with it, so typing in the search box above it
 * rerenders the box and not twenty rows (scalability rule 4): the page passes
 * the settled search to the query, never the draft to this table.
 */
export const DocumentsTable = memo(function DocumentsTable({
  rows,
  loading,
  refreshing = false,
  error,
  onRetry,
  page,
  total,
  onPageChange,
  range,
  writable,
  filtered,
  onAction,
}: DocumentsTableProps) {
  const columns = useMemo<Column<DocumentRow>[]>(
    () => [
      {
        key: 'document',
        header: 'Document',
        render: (row) => (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {row.title}
            </Typography>
            {/* The size rides under the name rather than in a column of its
                own, so the table fits beside the side rails at 1280 px. */}
            <Typography
              variant="caption"
              component="p"
              color="text.secondary"
              sx={{ overflowWrap: 'anywhere' }}
            >
              <span>{row.fileName}</span>
              {' · '}
              <span style={{ whiteSpace: 'nowrap' }}>{sizeInWords(row.sizeBytes)}</span>
            </Typography>
          </Box>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (row) => (
          <Box>
            <StatusChip
              label={stateWord(row)}
              tone={stateTone(row)}
              testId="documents-status-chip"
            />
            {replacementPending(row) ? (
              <Typography
                variant="caption"
                component="p"
                color="text.secondary"
                data-testid="documents-pending-caption"
                sx={{ mt: 0.5 }}
              >
                Replacement scanning
              </Typography>
            ) : null}
          </Box>
        ),
      },
      {
        key: 'category',
        header: 'Category',
        render: (row) => row.category?.name ?? 'Uncategorized',
      },
      {
        key: 'placement',
        header: 'ARC placement',
        render: (row) => placementWord(row.inArc),
      },
      {
        key: 'uploaded',
        header: 'Uploaded',
        render: (row) => formatDate(row.fileUploadedAt),
      },
      {
        key: 'views',
        header: `Views (${range} days)`,
        align: 'right',
        render: (row) => (
          <DocumentCount
            row={row}
            count={row.views}
            testId="documents-views-never-published-message"
          />
        ),
      },
      {
        key: 'downloads',
        header: `Downloads (${range} days)`,
        align: 'right',
        render: (row) => (
          <DocumentCount
            row={row}
            count={row.downloads}
            testId="documents-downloads-never-published-message"
          />
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        align: 'right',
        render: (row) => {
          const actions = actionsFor(row, writable);
          if (actions.length === 0) return null;
          const items: RowAction[] = actions.map((action) => ({
            id: action,
            label: ACTION_LABELS[action],
            onSelect: () => onAction(action, row),
          }));
          return (
            <RowActionsMenu
              rowLabel={row.title}
              actions={items}
              testId="documents-row-actions"
            />
          );
        },
      },
    ],
    [range, writable, onAction],
  );

  return (
    <DataTable
      caption="Documents"
      testId="documents-table"
      columns={columns}
      rows={rows}
      getRowKey={(row) => row.id}
      loading={loading}
      refreshing={refreshing}
      error={error}
      onRetry={onRetry}
      filtered={filtered}
      emptyMessage="No documents yet."
      noMatchMessage="No documents match. Clear the filters to see every document."
      page={page}
      pageSize={PAGE_SIZE}
      total={total}
      onPageChange={onPageChange}
    />
  );
});
